import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Sparkles } from 'lucide-react';

/**
 * DemoTabsHome — tabbed "Inside the live demo" switcher used on the Home page.
 *
 * Three tabs (Healthcare, NHS, Construction). Selecting a tab swaps the 4-tile
 * grid of representative screenshots. Construction is a "Coming soon" state
 * until screenshots are loaded.
 */
const TABS = [
  {
    key: 'hard-services',
    label: 'FM Hard Services',
    sublabel: 'Videra Healthcare',
    tagline: 'PFI / hard-services delivery, end-to-end.',
    href: '/sectors/hard-services',
    cta: 'See all 21 screens',
    folder: 'healthcare',
    available: true,
    tiles: [
      { f: 'hc-sector-dashboard.jpg', label: 'Sector Command Centre' },
      { f: 'hc-portfolio-tracker.jpg', label: 'Portfolio Tracker' },
      { f: 'hc-submittals.jpg', label: 'Submittals — PFI workflow' },
      { f: 'hc-projects-hs-gate.jpg', label: 'CDM evidence at gate' },
    ],
  },
  {
    key: 'nhs',
    label: 'NHS',
    sublabel: 'Videra NHS — Capital Programme',
    tagline: 'RIBA · KSAR · HAI-SCRIBE on one rail.',
    href: '/sectors/nhs',
    cta: 'See all 9 NHS screens',
    folder: 'nhs',
    available: true,
    tiles: [
      { f: 'nhs-portfolio-tracker.jpg', label: 'NHS Portfolio Tracker' },
      { f: 'nhs-board-pack.jpg', label: 'Trust Board Pack' },
      { f: 'nhs-compliance-rails.jpg', label: 'RIBA · KSAR · HAI-SCRIBE' },
      { f: 'nhs-pmo-command-centre.jpg', label: 'PMO Command Centre' },
    ],
  },
  {
    key: 'construction',
    label: 'Construction',
    sublabel: 'Videra Construction+',
    tagline: 'Tier-1 portfolio assurance & lifecycle.',
    href: '/sectors/construction',
    cta: 'See all 12 Construction+ screens',
    folder: 'construction',
    available: true,
    tiles: [
      { f: 'cp-portfolio-pmo.jpg', label: 'PMO Command Centre' },
      { f: 'cp-live-portfolio.jpg', label: 'Live portfolio · 52 projects' },
      { f: 'cp-lifecycle.jpg', label: 'Lifecycle — 5-Yr Plan Builder' },
      { f: 'cp-bid-pipeline.jpg', label: 'Bid pipeline & BidAI' },
    ],
  },
];

const DemoTabsHome = () => {
  const [active, setActive] = useState(TABS[0].key);
  const tab = TABS.find((t) => t.key === active) || TABS[0];

  return (
    <div className="mt-12 md:mt-16" data-testid="v2-home-demo-tabs">
      {/* Header + tab bar */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5 mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold mb-2">
            Inside the live demo
          </p>
          <h3 className="text-xl md:text-2xl font-medium tracking-tight text-slate-900">
            Real screens from{' '}
            <span className="text-slate-500">{tab.sublabel}.</span>
          </h3>
          <p className="mt-1 text-sm text-slate-600">{tab.tagline}</p>
        </div>

        {/* Tab bar */}
        <div
          className="inline-flex items-center bg-slate-100 rounded-full p-1 self-start lg:self-auto overflow-x-auto max-w-full"
          role="tablist"
          aria-label="Demo sector"
        >
          {TABS.map((t) => {
            const isActive = t.key === active;
            return (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setActive(t.key)}
                className={`relative px-4 md:px-5 py-2 rounded-full text-xs md:text-sm font-medium whitespace-nowrap transition-colors ${
                  isActive
                    ? 'text-slate-900'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
                data-testid={`v2-home-demo-tab-${t.key}`}
              >
                {isActive && (
                  <motion.span
                    layoutId="v2-home-demo-tab-pill"
                    className="absolute inset-0 bg-white shadow-sm rounded-full"
                    transition={{ type: 'spring', bounce: 0.2, duration: 0.45 }}
                  />
                )}
                <span className="relative inline-flex items-center gap-2">
                  {t.label}
                  {!t.available && (
                    <span className="text-[9px] uppercase tracking-wider bg-slate-200 text-slate-600 rounded-full px-1.5 py-0.5">
                      Soon
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Active tab actions */}
      <div className="flex items-center justify-end mb-5">
        {tab.available ? (
          <Link
            to={tab.href}
            className="inline-flex items-center text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors group"
            data-testid={`v2-home-demo-link-${tab.key}`}
          >
            {tab.cta}
            <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        ) : (
          <span className="inline-flex items-center text-sm text-slate-500">
            <Sparkles className="w-4 h-4 mr-1.5" />
            {tab.cta}
          </span>
        )}
      </div>

      {/* Grid */}
      <AnimatePresence mode="wait">
        {tab.available ? (
          <motion.div
            key={tab.key}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.25 }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4"
            data-testid={`v2-home-demo-grid-${tab.key}`}
          >
            {tab.tiles.map((it) => (
              <Link
                to={tab.href}
                key={it.f}
                className="group block bg-slate-900 rounded-xl overflow-hidden border border-slate-200 hover:border-slate-400 hover:shadow-lg transition-all"
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={`/images/${tab.folder}/${it.f}`}
                    alt={it.label}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-500"
                  />
                </div>
                <div className="px-3 py-2.5 bg-white">
                  <p className="text-[12px] md:text-sm font-medium text-slate-900 leading-tight">
                    {it.label}
                  </p>
                </div>
              </Link>
            ))}
          </motion.div>
        ) : (
          <motion.div
            key="coming-soon"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="rounded-xl border border-dashed border-slate-300 bg-slate-50/60 p-10 md:p-14 text-center"
            data-testid={`v2-home-demo-grid-${tab.key}`}
          >
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white border border-slate-200 text-slate-500 mb-4">
              <Sparkles className="w-5 h-5" />
            </div>
            <h4 className="text-lg md:text-xl font-medium text-slate-900">
              Videra Construction+ screens — coming soon.
            </h4>
            <p className="mt-2 text-sm text-slate-600 max-w-xl mx-auto">
              We&apos;re curating the next batch of demo screenshots from the
              Tier-1 construction workspace. Drop your details on the contact
              page and we&apos;ll let you know the moment they go live.
            </p>
            <Link
              to="/contact"
              className="mt-5 inline-flex items-center text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors group"
              data-testid="v2-home-demo-construction-notify"
            >
              Get notified
              <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DemoTabsHome;
